// The chrome every course demo sits in: a 3D stage on one side, a code panel
// and its controls on the other.
//
// Each course makes the same argument in its own material — you do a thing by
// hand, then you write the rule and the machine does it — so the frame around
// that argument is worth having exactly once.
//
// Two rules the shell enforces for every demo:
//
//  · Nothing lives only in the canvas. The controls, the status line and the
//    code are plain DOM, so the demo works with a keyboard, with a screen
//    reader, and on a device where WebGL never starts.
//  · The canvas is an upgrade, not the page. It mounts when the section scrolls
//    into view, and never on Save-Data, 2G, or a browser without WebGL — ad
//    traffic arrives on mid-range phones over cellular.
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { RotateCcw } from 'lucide-react';
import { T } from '../../../utils/fonts';

const canRender3D = () => {
  if (typeof window === 'undefined') return false;
  const connection = navigator.connection;
  if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) return false;
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
};

const useInViewOnce = (ref) => {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || seen) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return undefined; }
    const observer = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setSeen(true); },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, seen]);
  return seen;
};

/**
 * @param {object}   camera    r3f camera config for the stage.
 * @param {node}     scene     Canvas contents. Rendered only once 3D is on.
 * @param {node}     fallback  Flat stand-in shown in the stage instead.
 * @param {node}     status    The pill over the stage. Announced politely.
 * @param {object}   code      { tag, lines: string[], activeLine: number|null }
 * @param {node}     controls  Buttons or inputs under the code.
 * @param {node}     hint      One line of explanation under the controls.
 * @param {function} onReset   Omit to hide the reset control entirely.
 */
const DemoShell = ({
  camera,
  scene,
  fallback,
  status,
  code,
  controls,
  hint,
  onReset,
  resetDisabled,
  resetLabel = 'התחלה מחדש',
}) => {
  const hostRef = useRef(null);
  const [ready] = useState(canRender3D);
  const inView = useInViewOnce(hostRef);
  const show3D = ready && inView;

  return (
    <div ref={hostRef} className="demo">
      <div className="demo-stage webgl-interaction-surface">
        {show3D ? (
          <Canvas
            dpr={[1, 2]}
            gl={{ antialias: true, powerPreference: 'high-performance' }}
            camera={camera}
            style={{ width: '100%', height: '100%' }}
          >
            <Suspense fallback={null}>{scene}</Suspense>
          </Canvas>
        ) : (
          fallback
        )}

        {status && <p className="demo-status" aria-live="polite">{status}</p>}
      </div>

      <div className="demo-panel gg-card">
        {code && (
          <>
            <p className="gg-hud">{code.tag}</p>
            <pre className="demo-code" dir="ltr" aria-label={code.label}>
              {code.lines.map((line, i) => (
                <code key={line + i} className={i === code.activeLine ? 'line active' : 'line'}>
                  {line}
                </code>
              ))}
            </pre>
          </>
        )}

        {controls && <div className="demo-actions">{controls}</div>}
        {hint && <p className="demo-hint">{hint}</p>}

        {onReset && (
          <button type="button" className="demo-reset" onClick={onReset} disabled={resetDisabled}>
            <RotateCcw size={14} aria-hidden="true" /> {resetLabel}
          </button>
        )}
      </div>

      <style>{`
        .demo {
          display: grid;
          grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
          gap: clamp(16px, 3vw, 28px);
          align-items: stretch;
        }
        .demo-stage {
          position: relative;
          min-height: clamp(280px, 46vw, 420px);
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid ${T.line};
          /* The canvas is transparent, so this gradient is the sky the scene
             sits against — and it has to be a real surface in both themes, not
             a night sky that fades to white on the light one. */
          background:
            radial-gradient(90% 70% at 50% 30%, var(--demo-glow, rgba(124,189,74,0.10)), transparent 70%),
            linear-gradient(180deg, #0D1426, #070B16);
          touch-action: manipulation;
        }
        :root[data-theme="light"] .demo-stage {
          background:
            radial-gradient(90% 70% at 50% 28%, var(--demo-glow-light, rgba(124,189,74,0.18)), transparent 70%),
            linear-gradient(180deg, #DCEDF9, #F5FBFF);
        }

        /* Bottom-left of the stage: the top corner belongs to the floating
           accessibility button, and the models tend to occupy the right. */
        .demo-status {
          position: absolute; inset-inline-end: 14px; bottom: 12px; margin: 0;
          display: inline-flex; align-items: center; gap: 8px;
          padding: 7px 14px; border-radius: 99px;
          background: ${T.panelGlass};
          border: 1px solid ${T.line};
          font-size: 0.85rem; font-weight: 700; color: ${T.text};
          max-inline-size: none;
        }
        .demo-status strong { color: var(--demo-accent, ${T.minecraft}); font-size: 1rem; }
        .demo-status em { font-style: normal; color: var(--demo-accent, ${T.minecraft}); }

        .demo-panel {
          padding: clamp(16px, 3vw, 22px);
          display: flex; flex-direction: column; gap: 14px;
        }
        .demo-code {
          margin: 0; padding: 14px 16px; border-radius: 12px;
          background: ${T.panelUp}; border: 1px solid ${T.lineSoft};
          font-family: ${T.fontHud}; font-size: clamp(0.66rem, 1.8vw, 0.78rem);
          line-height: 1.9; overflow-x: auto; white-space: pre;
          /* dir="ltr" fixes the writing direction but not the alignment, which
             is inherited from the RTL page — code has to start at the left. */
          text-align: left;
        }
        .demo-code .line { display: block; color: ${T.muted}; border-radius: 4px; }
        .demo-code .line.active {
          color: ${T.text};
          background: var(--demo-code-wash, rgba(124,189,74,0.16));
          box-shadow: inset 3px 0 0 var(--demo-accent, ${T.minecraft});
        }
        .demo-actions { display: flex; flex-wrap: wrap; gap: 10px; }
        .demo-actions > * { flex: 1 1 150px; }
        .demo-hint { margin: 0; font-size: 0.86rem; color: ${T.muted}; line-height: 1.6; }
        .demo-reset {
          align-self: flex-start; margin-top: auto;
          display: inline-flex; align-items: center; gap: 6px;
          background: none; border: none; padding: 6px 0; cursor: pointer;
          color: ${T.faint}; font-family: ${T.fontBody}; font-weight: 700; font-size: 0.82rem;
        }
        .demo-reset:hover:not(:disabled) { color: ${T.text}; }
        .demo-reset:disabled { opacity: 0.4; cursor: default; }
        .demo-reset:focus-visible { outline: 2px solid ${T.focus}; outline-offset: 3px; border-radius: 4px; }

        /* Flat stand-in tiles, for the demos that use one instead of WebGL. */
        .demo-flat {
          position: absolute; inset: 0;
          display: grid; gap: 10px; padding: clamp(40px, 9%, 72px);
          align-content: center;
        }
        .demo-flat .tile {
          aspect-ratio: 1; border-radius: 6px;
          background: #5B6B7A; border: 2px solid var(--demo-accent, ${T.minecraft});
          transition: transform .3s ease, opacity .3s ease;
        }
        .demo-flat .tile.gone { transform: scale(0.2) rotate(35deg); opacity: 0; }

        @media (max-width: 820px) {
          .demo { grid-template-columns: minmax(0, 1fr); }
        }
      `}</style>
    </div>
  );
};

export default DemoShell;
